// A page's code that no longer exists (2026-10-19). Every page is its own chunk, loaded on first visit; a deploy
// replaces them, so a tab opened before it asked for a file that was gone, CloudFront answered index.html, and the
// navigation failed silently: the sidebar "did nothing" until a reload. Now a page whose code fails to load is loaded
// once in full (which fetches the new build); if it fails again, the shell says so instead of retrying forever.
import { ref } from "vue";
import { isNavigationFailure, NavigationFailureType, type Router } from "vue-router";

const KEY = "majlis.chunk-reload";
const CHUNK_ERRORS = [
  "Failed to fetch dynamically imported module",   // Chromium
  "error loading dynamically imported module",     // Firefox
  "Importing a module script failed",              // Safari
  "Unable to preload CSS",                         // Vite's own preload of a page's styles
  "Expected a JavaScript-or-Wasm module script",   // the file came back as HTML
  "Expected a JavaScript module script",
];

/** The page that couldn't load even after a full load, for the shell's message; null when all is well. */
export const pageLoadFailure = ref<string | null>(null);

export function isChunkLoadError(error: unknown): boolean {
  const text = String((error as { message?: unknown } | null)?.message ?? error ?? "");
  return CHUNK_ERRORS.some((m) => text.includes(m));
}

interface Env { storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null; replace: (href: string) => void }

function storageOf(): Env["storage"] {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function installChunkRecovery(router: Router, env: Env = { storage: storageOf(), replace: (h) => window.location.replace(h) }) {
  router.onError((error, to) => {
    if (!isChunkLoadError(error)) return;
    const href = router.resolve(to).href;   // its query and hash too
    let tried: string | null = null;
    try {
      tried = env.storage?.getItem(KEY) ?? null;
      if (tried !== href) env.storage?.setItem(KEY, href);
    } catch {
      tried = href;   // no storage: never reload blind (a loop), say so instead
    }
    if (tried === href || !env.storage) {
      pageLoadFailure.value = href;
      return;
    }
    env.replace(href);
  });
  router.afterEach((_to, _from, failure) => {   // a page reached (or the one they're on, asked again) clears it
    if (failure && !isNavigationFailure(failure, NavigationFailureType.duplicated)) return;
    pageLoadFailure.value = null;
    try {
      env.storage?.removeItem(KEY);
    } catch {
      /* nothing to clear */
    }
  });
}

/** The person's own retry: a fresh full load of the page they asked for. */
export function retryPageLoad(env: Env = { storage: storageOf(), replace: (h) => window.location.replace(h) }) {
  const href = pageLoadFailure.value;
  if (!href) return;
  try {
    env.storage?.removeItem(KEY);
  } catch {
    /* a reload is still the retry */
  }
  env.replace(href);
}
